/**
 * ConversationPane — the RIGHT half of the workspace: the item's scoped
 * Merlin conversation, with the FULL composer (mic, attachments, Stop,
 * spoken rundown) — the same engine and components as the Ask Merlin tab.
 *
 * Ownership: the console door scopes conversation reads AND the composer to
 * the conversation's owner (the routee). A viewer who isn't the owner — an
 * admin reading the Done tab — sees whose it is instead of a 404.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { trackEvent } from '../../services/portalTracking';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { CopilotComposer } from '../../components/quality/CopilotComposer';
import { CopilotTurnList } from '../../components/quality/CopilotTurnList';
import { useCopilotChat } from '../../components/quality/useCopilotChat';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';
import styles from './QualityWorkspace.module.css';

export interface ConversationPaneProps {
  /** The conversation to load, or null when the item has none yet. */
  conversationId: string | null;
  /** False when the backend says the viewer does not own it. */
  ownedByViewer: boolean;
  /** Whose conversation it is, for the locked-composer copy. */
  ownerLabel: string | null;
  userId: string | null;
  isAdmin: boolean;
  /** Hide the pipeline's seeded kickoff (thread investigations). */
  hideSystemSeeded?: boolean;
  /** What the composer says when there is no conversation yet. */
  noConversationCopy: string;
  /** The item's number (INV-/QT-), quoted when the conversation can't be opened. */
  itemNo?: string;
  /** Text the brief asks to be put in the message box (not sent); a new
   *  `n` places it again. */
  starter?: { text: string; n: number };
  /** The item's asks: Merlin's "Drafted a question to X" lines get a live
   *  sent / answered chip (proposal 2). */
  asks?: DoorDataRequestRead[];
  /** Under the "Who we've asked" panel (proposal 2): the transcript folds
   *  behind its header, closed while this is true, until the reader opens
   *  it; the composer stays in view either way. Absent: never folds. */
  foldedByDefault?: boolean;
  /** Recorded with the fold toggle. */
  trackContext?: { thread_no?: string | null; investigation_no?: string | null };
}

export function ConversationPane({
  conversationId,
  ownedByViewer,
  ownerLabel,
  userId,
  isAdmin,
  hideSystemSeeded = false,
  noConversationCopy,
  itemNo,
  starter,
  asks,
  foldedByDefault,
  trackContext,
}: ConversationPaneProps) {
  // A viewer may ask Merlin, not have Merlin ask someone (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  const usable = !!conversationId && ownedByViewer;
  // null until the reader toggles: follows foldedByDefault (which flips once
  // the asks load), then stays where the reader put it.
  const [openChoice, setOpenChoice] = useState<boolean | null>(null);
  const foldable = foldedByDefault !== undefined;
  const open = !foldable || (openChoice ?? !foldedByDefault);
  const chat = useCopilotChat({ userId, rememberActive: false, createOnDemand: false });
  const { loadConversation, resetConversation } = chat;
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const { setTextValue } = chat;
  const starterText = starter?.text ?? '';
  const starterN = starter?.n ?? 0;

  // The starter lands in the box for the reader to change or send; it is
  // never sent from here.
  useEffect(() => {
    if (!starterN || !starterText) return;
    setTextValue(starterText);
    inputRef.current?.focus();
  }, [starterN, starterText, setTextValue]);

  useEffect(() => {
    if (conversationId && ownedByViewer) {
      void loadConversation(conversationId);
    } else {
      resetConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the target changes
  }, [conversationId, ownedByViewer]);

  // Sending from the folded chat opens it, so the reply is not hidden.
  useEffect(() => {
    if (chat.busy && foldable) setOpenChoice(true);
  }, [chat.busy, foldable]);

  // The conversation could not be read: on a case the backend does not say
  // whose it is, so a conversation started from another account only shows
  // up as a failed load. A chat box that takes a question and then fails is
  // worse than none, so say plainly where to ask instead.
  if (usable && chat.loadFailed) {
    return (
      <aside
        className={`${styles.chat} ${styles.chatLocked}`}
        aria-label="Merlin conversation"
        data-testid="chat-unreadable-note"
      >
        <strong>Merlin’s conversation on this item</strong>
        <span>
          It was started from another account (or has been removed), so it can’t be
          opened here.
        </span>
        <span>
          To ask Merlin about it, or have Merlin ask a colleague for you, use{' '}
          <Link to="../ask">Ask Merlin</Link>
          {itemNo ? ` and mention ${itemNo}.` : '.'}
        </span>
      </aside>
    );
  }

  const lockedReason = !conversationId
    ? noConversationCopy
    : !ownedByViewer
      ? `Only ${ownerLabel ?? 'the routee'} can ask Merlin on this thread.`
      : null;

  const earlier = chat.turns.filter((t) => !(hideSystemSeeded && t.role === 'user' && t.systemSeeded)).length;
  const toggle = () => {
    const next = !open;
    setOpenChoice(next);
    trackEvent('action', 'asks_panel_chat_toggle', {
      ...trackContext,
      conversation_id: conversationId,
      open: next,
      result: next ? 'panel_opened' : 'panel_closed',
    });
  };

  return (
    <div
      className={`${styles.chat} ${open ? '' : styles.chatFolded}`}
      aria-label="Merlin conversation"
    >
      <div className={styles.chatHead}>
        {foldable ? (
          <button
            type="button"
            className={styles.chatFold}
            aria-expanded={open}
            onClick={toggle}
            data-testid="chat-fold"
          >
            <span className={styles.chatTitle}>Ask Merlin about this</span>
            <span className={styles.chatSub}>
              {' '}
              · {earlier} earlier message{earlier === 1 ? '' : 's'} {open ? '▴' : '▾'}
            </span>
          </button>
        ) : (
          <span className={styles.chatTitle}>Ask Merlin about this</span>
        )}
        <span className={styles.chatSub}>
          {usable
            ? 'Ask a question, or have Merlin ask a colleague for you'
            : lockedReason}
        </span>
      </div>
      {conversationId && !ownedByViewer && (
        <p className={styles.foreignNote} data-testid="foreign-conversation-note">
          Merlin’s conversation on this thread belongs to {ownerLabel ?? 'another user'}.
          Only they can read it or ask Merlin about it here.
        </p>
      )}
      {chat.error && (
        <div className={styles.error} role="alert">
          {chat.error}
        </div>
      )}
      {open && (
        <CopilotTurnList
          askTrackContext={trackContext}
          variant="pane"
          compactVoice
          turns={chat.turns}
          loading={chat.loadingConvo}
          hideSystemSeeded={hideSystemSeeded}
          onReplay={chat.replay}
          onStop={chat.stopSpeaking}
          speakingId={chat.speakingId}
          isSpeaking={chat.isSpeaking}
          isSynthesizing={chat.isSynthesizing}
          showUsage={isAdmin}
          conversationId={usable ? conversationId : null}
          strongestModelId={chat.strongestModel || null}
          strongestModelLabel={
            chat.models.find((m) => m.id === chat.strongestModel)?.label ?? null
          }
          emptyState={
            usable ? (
              <p className={styles.chatEmpty}>
                Nothing said here yet — ask Merlin about this thread below.
              </p>
            ) : null
          }
          asks={asks}
        />
      )}
      <CopilotComposer
        compact
        value={chat.textValue}
        onChange={chat.setTextValue}
        onSubmit={chat.submitTyped}
        onAudio={(r) => void chat.handleAudio(r)}
        onStop={() => void chat.handleStop()}
        busy={chat.busy}
        transcribing={chat.transcribing}
        cancelling={chat.cancelling}
        attach={chat.attach}
        disabledReason={lockedReason}
        placeholder={readOnly ? "Ask Merlin about this…" : "Ask, or have Merlin ask someone…"}
        inputRef={inputRef}
      />
    </div>
  );
}

export default ConversationPane;
